import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useGoals } from '../../contexts/GoalContext';
import { useChecklists } from '../../contexts/ChecklistContext';
import { useExpenses } from '../../contexts/ExpenseContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useCharacterState } from '../../contexts/CharacterContext';
import { api } from '../../services/api';
import { getTodayString } from '../../utils/dateUtils';
import { isCannedGreeting, getCannedGreeting } from '../../utils/cannedGreetings';
import { AIAvatar } from './AIAvatar';
import { CharacterChatBubble } from './CharacterChatBubble';

const MAX_TOOL_ITERATIONS = 5;

function summarizeGoal(t, input) {
  return t('summarizeGoal', { title: input.title, target: input.targetAmount, unit: input.category === 'money' ? ' ₫' : '', endDate: input.endDate });
}

function summarizeChecklist(t, input) {
  return t('summarizeChecklist', { title: input.title, count: input.tasks.length, plural: input.tasks.length === 1 ? '' : 's' });
}

function summarizeBudget(t, input) {
  return t('summarizeBudget', { type: input.type || 'Expense', amount: input.amount, category: input.category });
}

function loadStoredChat(storageKey) {
  try {
    const saved = window.sessionStorage.getItem(storageKey);
    if (!saved) return { apiMessages: [], displayItems: [] };
    const parsed = JSON.parse(saved);
    // A confirmation left pending across a refresh can never resolve again -
    // the Promise it was gating lived only in the previous page's memory.
    const displayItems = (parsed.displayItems || []).map((item) => (
      item.sender === 'confirm' && item.pending
        ? { ...item, pending: false, expired: true }
        : item
    ));
    return { apiMessages: parsed.apiMessages || [], displayItems };
  } catch {
    return { apiMessages: [], displayItems: [] };
  }
}

function ChatWidget() {
  const { id: userId, role, assistantPersonality } = useAuth();
  const { addGoal } = useGoals();
  const { addChecklist } = useChecklists();
  const { addBudget, budgets } = useExpenses();
  const { openUpgradeModal } = useSubscription();
  const { mood, setMood, flashMood, pendingPrompt, dismissPrompt } = useCharacterState();
  const { t } = useTranslation('chat');
  const { t: tSub } = useTranslation('subscription');

  const storageKey = `zlux-chat-${userId || 'guest'}`;
  const [isOpen, setIsOpen] = useState(false);
  const [apiMessages, setApiMessages] = useState(() => loadStoredChat(storageKey).apiMessages);
  const [displayItems, setDisplayItems] = useState(() => loadStoredChat(storageKey).displayItems);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [usage, setUsage] = useState(null);
  const bodyRef = useRef(null);
  const confirmResolvers = useRef({});

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [displayItems, isSending]);

  // Keeps the conversation alive across a page refresh instead of silently
  // dropping it - scoped per user so switching accounts in one tab can't leak
  // one person's chat history into another's.
  useEffect(() => {
    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify({ apiMessages, displayItems }));
    } catch {
      // Storage full/unavailable - chat still works, just won't survive a refresh.
    }
  }, [storageKey, apiMessages, displayItems]);

  useEffect(() => {
    if (role !== 'user' && role !== 'special') return;
    let cancelled = false;
    api.get('/chat/usage')
      .then((data) => { if (!cancelled) setUsage(data); })
      .catch(() => { if (!cancelled) setUsage(null); });
    return () => {
      cancelled = true;
    };
  }, [role]);

  if (role !== 'user' && role !== 'special') return null;

  const pushDisplay = (item) => {
    const id = item.id || `${Date.now()}-${Math.random()}`;
    setDisplayItems((current) => [...current, { ...item, id }]);
    return id;
  };

  // Shows the user exactly what will be created - including the auto-converted
  // VND amount - before it's actually committed, rather than silently trusting
  // the model's currency conversion.
  const requestConfirmation = (question) => {
    const id = pushDisplay({ sender: 'confirm', text: question, pending: true, confirmed: null });
    return new Promise((resolve) => {
      confirmResolvers.current[id] = resolve;
    });
  };

  const resolveConfirm = (id, confirmed) => {
    const resolve = confirmResolvers.current[id];
    if (!resolve) return;
    delete confirmResolvers.current[id];
    resolve(confirmed);
    setDisplayItems((current) => current.map((item) => (
      item.id === id ? { ...item, pending: false, confirmed } : item
    )));
  };

  const runTool = async (block) => {
    const { name, input: toolInput } = block;
    try {
      if (name === 'create_goal') {
        const goal = {
          title: toolInput.title,
          description: toolInput.description || '',
          category: toolInput.category === 'money' ? 'money' : 'activity',
          startDate: toolInput.startDate,
          endDate: toolInput.endDate,
          targetAmount: Number(toolInput.targetAmount) || 0,
          achievedAmount: Number(toolInput.achievedAmount) || 0,
          status: toolInput.status || 'Not Started',
        };
        if (goal.category === 'money') {
          const confirmed = await requestConfirmation(t('confirmCreateGoal', {
            title: goal.title, target: goal.targetAmount, endDate: goal.endDate,
          }));
          if (!confirmed) {
            return { content: 'The user declined this action in the confirmation prompt; nothing was created.', isError: true };
          }
        }
        await addGoal(goal);
        pushDisplay({ sender: 'system', text: `✓ ${summarizeGoal(t, goal)}` });
        return { content: 'Goal created successfully.' };
      }

      if (name === 'create_checklist') {
        const tasks = (toolInput.tasks || []).map((text, index) => ({
          id: Date.now() + index,
          text,
          completed: false,
        }));
        const checklist = {
          title: toolInput.title,
          description: toolInput.description || '',
          date: toolInput.date,
          reminderType: toolInput.reminderType === 'daily' ? 'daily' : 'once',
          tasks,
        };
        await addChecklist(checklist);
        pushDisplay({ sender: 'system', text: `✓ ${summarizeChecklist(t, checklist)}` });
        return { content: 'Checklist created successfully.' };
      }

      if (name === 'create_budget') {
        const type = toolInput.type || 'Expense';
        const conflict = budgets.some((b) => b.category === toolInput.category && b.type === type);
        if (conflict) {
          return { content: t('budgetConflict', { type, category: toolInput.category }), isError: true };
        }
        const budget = { category: toolInput.category, amount: Number(toolInput.amount) || 0, type };
        const confirmed = await requestConfirmation(t('confirmCreateBudget', {
          type, amount: budget.amount, category: budget.category,
        }));
        if (!confirmed) {
          return { content: 'The user declined this action in the confirmation prompt; nothing was created.', isError: true };
        }
        await addBudget(budget);
        pushDisplay({ sender: 'system', text: `✓ ${summarizeBudget(t, budget)}` });
        return { content: 'Budget created successfully.' };
      }

      return { content: t('unknownTool', { name }), isError: true };
    } catch (error) {
      return { content: error.message || t('somethingWrong'), isError: true };
    }
  };

  const sendToAssistant = async (nextApiMessages, iteration = 0) => {
    if (iteration >= MAX_TOOL_ITERATIONS) {
      pushDisplay({ sender: 'system', text: t('tooManySteps') });
      flashMood('surprised');
      setIsSending(false);
      return;
    }

    let response;
    try {
      response = await api.post('/chat', {
        messages: nextApiMessages,
        context: { today: getTodayString(), categories: [...new Set(budgets.map((budget) => budget.category))] },
      });
    } catch (error) {
      pushDisplay({ sender: 'error', text: error.message, code: error.code });
      flashMood('sad');
      setIsSending(false);
      return;
    }

    const { content, stopReason, remaining } = response;
    if (typeof remaining === 'number') {
      setUsage((current) => (current ? { ...current, remaining, used: current.limit - remaining } : current));
    }
    const textBlocks = content.filter((block) => block.type === 'text' && block.text.trim());
    textBlocks.forEach((block) => pushDisplay({ sender: 'assistant', text: block.text }));

    const messagesWithAssistant = [...nextApiMessages, { role: 'assistant', content }];

    if (stopReason === 'refusal') {
      pushDisplay({ sender: 'error', text: t('refusal') });
      flashMood('surprised');
      setApiMessages(messagesWithAssistant);
      setIsSending(false);
      return;
    }

    const toolUseBlocks = content.filter((block) => block.type === 'tool_use');
    if (toolUseBlocks.length === 0) {
      flashMood('happy');
      setApiMessages(messagesWithAssistant);
      setIsSending(false);
      return;
    }

    // Sequential, not Promise.all: a confirmation prompt pauses this loop until
    // the user answers, and running one at a time keeps at most one dialog on
    // screen instead of stacking several simultaneous confirm cards.
    const toolResults = [];
    for (const block of toolUseBlocks) {
      // eslint-disable-next-line no-await-in-loop
      const result = await runTool(block);
      toolResults.push({
        type: 'tool_result',
        tool_use_id: block.id,
        content: result.content,
        is_error: !!result.isError,
      });
    }

    if (toolResults.some((result) => result.is_error)) {
      flashMood('surprised');
    }

    const messagesWithResults = [...messagesWithAssistant, { role: 'user', content: toolResults }];
    setApiMessages(messagesWithResults);
    await sendToAssistant(messagesWithResults, iteration + 1);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const text = input.trim();
    if (!text || isSending) return;
    if (usage && !usage.isPro && usage.remaining === 0) return;

    setInput('');
    pushDisplay({ sender: 'user', text });

    // Token/cost optimization: a bare greeting gets an instant local reply instead of a
    // real Gemini call - no credit consumed, no network round trip. Still recorded into
    // apiMessages so it stays in context if the user asks something real right after.
    if (isCannedGreeting(text)) {
      const canned = getCannedGreeting(assistantPersonality);
      pushDisplay({ sender: 'assistant', text: canned });
      flashMood('happy');
      setApiMessages((current) => [
        ...current,
        { role: 'user', content: text },
        { role: 'assistant', content: [{ type: 'text', text: canned }] },
      ]);
      return;
    }

    setIsSending(true);
    setMood('thinking');

    const nextApiMessages = [...apiMessages, { role: 'user', content: text }];
    setApiMessages(nextApiMessages);
    await sendToAssistant(nextApiMessages);
  };

  const handleFabClick = () => {
    if (pendingPrompt) dismissPrompt();
    setIsOpen((open) => !open);
  };

  const isExhausted = !!(usage && !usage.isPro && usage.remaining === 0);

  return (
    <div className="chat-widget">
      {isOpen && (
        <div className="chat-panel">
          <div className="chat-panel-header">
            <span>{t('title')}</span>
            <button type="button" className="chat-close-btn" onClick={() => setIsOpen(false)} aria-label={t('closeAria')}>✕</button>
          </div>
          <div className="chat-panel-body" ref={bodyRef}>
            {displayItems.length === 0 && (
              <p className="chat-empty-hint">{t('emptyHint')}</p>
            )}
            {displayItems.map((item, index) => {
              if (item.sender === 'assistant') {
                return <CharacterChatBubble key={item.id} text={item.text} animate={index === displayItems.length - 1} />;
              }
              return (
              <div key={item.id} className={`chat-message chat-message-${item.sender}`}>
                {item.text}
                {item.sender === 'confirm' && item.pending && (
                  <div className="chat-confirm-actions">
                    <button type="button" className="chat-confirm-btn" onClick={() => resolveConfirm(item.id, true)}>
                      {t('confirmButton')}
                    </button>
                    <button type="button" className="chat-cancel-btn" onClick={() => resolveConfirm(item.id, false)}>
                      {t('cancelButton')}
                    </button>
                  </div>
                )}
                {item.sender === 'confirm' && !item.pending && (
                  <div className="chat-confirm-outcome">
                    {item.expired ? t('confirmExpired') : (item.confirmed ? t('confirmAccepted') : t('confirmDeclined'))}
                  </div>
                )}
                {item.code === 'AI_LIMIT_REACHED' && (
                  <button type="button" className="chat-upgrade-link" onClick={() => openUpgradeModal('ai')}>
                    {tSub('chat.upgradeLink')}
                  </button>
                )}
              </div>
              );
            })}
            {isSending && <div className="chat-message chat-message-assistant chat-message-typing">…</div>}
          </div>
          {usage && !usage.isPro && (
            <div className={`chat-usage-indicator${isExhausted ? ' chat-usage-empty' : ''}`}>
              {t('creditsRemaining', { remaining: usage.remaining, limit: usage.limit })}
              {isExhausted && (
                <button type="button" className="chat-upgrade-link" onClick={() => openUpgradeModal('ai')}>
                  {tSub('chat.upgradeLink')}
                </button>
              )}
            </div>
          )}
          <form className="chat-panel-input" onSubmit={handleSubmit}>
            <input
              type="text"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder={t('inputPlaceholder')}
              disabled={isSending || isExhausted}
            />
            <button type="submit" className="chat-send-btn" disabled={isSending || isExhausted || !input.trim()} aria-label={t('sendAria')}>➤</button>
          </form>
        </div>
      )}
      <div className="chat-fab-wrapper">
        {pendingPrompt && !isOpen && (
          <button type="button" className="character-prompt-bubble" onClick={handleFabClick}>
            {pendingPrompt.text}
          </button>
        )}
        <button
          type="button"
          className="chat-fab"
          onClick={handleFabClick}
          aria-label={isOpen ? t('closeChatAria') : t('openAria')}
        >
          <AIAvatar mood={mood} size={56} />
        </button>
      </div>
    </div>
  );
}

export default ChatWidget;
